import { useFeatureFlagEnabled } from "@client/hooks/use-feature-flag-enabled";
import { useEffect, useMemo } from "react";
import { Capacitor } from "@capacitor/core";
import { isIPad } from "@client/lib/isIPad";
import { ConnectionStatusDisplay } from "@client/components/utils/ConnectionStatusDisplay";
import { DevBuildInfo } from "@client/components/utils/DevBuildInfo";
import { ArchivedRoundBanner } from "@client/components/utils/ArchivedRoundBanner";
import { OfflineBanner } from "@client/components/utils/OfflineBanner";
import {
  ImpersonateAction,
  ImpersonationBanner,
} from "@client/components/utils/HeaderImpersonation";
import { PresenceAvatars } from "@client/components/utils/PresenceAvatars";
import { hideLiveHeaderIndicators } from "@client/lib/e2eVisualMode";
import { getHeaderEnvironmentTintClass } from "@client/lib/environmentTint";
import { useEffectiveUser } from "@client/hooks/use-effective-user";
import {
  useEffectiveRoundPhases,
  useUnderlyingRoundPhases,
} from "@client/hooks/use-round-phase";
import { useVisitedNavItems } from "@client/hooks/use-visited-nav-items";
import {
  useImpersonateContext,
  useRoundAdmin,
} from "@client/ImpersonateContext";
import {
  getNavVisibility,
  PHASE_LABEL,
  pickEffectivePhase,
} from "@client/utils/navPhaseVisibility";
import {
  buildRoundAdminOrFunderNavItems,
  isRoundAdminNavItemVisible,
} from "@client/utils/roundAdminNav";
import { buildGlobalNavEntries } from "@client/utils/globalPrimaryNav";
import {
  buildRoundNavEntries,
  type RoundNavLinkDef,
} from "@client/utils/roundPrimaryNav";
import {
  rememberFunderTabPath,
  type WorkspaceTab,
} from "@client/utils/funderTabMemory";
import { QueueDrawer } from "@client/pages/round/funder-workspace/queue/QueueDrawer";
import { TodoDrawerTrigger } from "@client/pages/round/funder-workspace/todo/TodoDrawerTrigger";
import LoginButton from "@lib/auth/client/Login/LoginButton";
import { getLogoutLink } from "@lib/auth/client/getLogoutLink";
import { isDeveloperEmail } from "@lib/developers";
import {
  isRestrictedFunderViewer,
  roundUsesTracks,
  roundAllowsAnonymization,
} from "@engine/round/accessors";
import {
  AppBar,
  type AppBarNavItem,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@sfc/ui";
import {
  NotebookPenIcon,
  Settings,
  SettingsIcon,
  ShieldCheckIcon,
  UserIcon,
  WrenchIcon,
} from "lucide-react";
import {
  GENERAL_NOTES_NAV_KEY,
  openGeneralNotes,
} from "@client/components/round/generalNotesStore";
import { Link, useLocation } from "wouter";
import { EnvironmentBadgedLogo } from "../logo";

function OnboardingBanner({ roundId }: { roundId: string }) {
  return (
    <div className="bg-blue-500/10 border-b border-blue-500/30 px-4 py-2 text-sm flex items-center justify-center gap-2">
      <span>Finish onboarding to get ready for Initial Evals.</span>
      <Link
        href={`/round/${roundId}/onboarding`}
        className="font-semibold text-blue-600 dark:text-blue-400 hover:underline"
      >
        Continue
      </Link>
    </div>
  );
}

function AdminOrFunderGear({ roundId }: { roundId: string }) {
  const [location] = useLocation();
  const { isAdmin, isFunder } = useRoundAdmin();
  const { value } = useImpersonateContext();
  const currentPhases = useUnderlyingRoundPhases();

  const roundName = value?.round.name ?? "Round";

  const adminNavItems = buildRoundAdminOrFunderNavItems(
    roundId,
    false,
    value?.round.isSfcRound ?? true,
    value ? roundUsesTracks(value.round) : true,
    value ? roundAllowsAnonymization(value.round) : true,
  ).filter((item) =>
    isRoundAdminNavItemVisible(item, {
      isAdmin,
      isFunder,
      currentPhases,
    }),
  );

  if (adminNavItems.length === 0) return null;

  const base = `/round/${roundId}`;
  const isAdminPath =
    location.startsWith(`${base}/admin`) ||
    location.startsWith(`${base}/logs`) ||
    location.startsWith(`${base}/export-data`) ||
    location.startsWith(`${base}/settings`);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <button
            className={cn(
              "flex items-center justify-center h-9 w-9 rounded-full outline-none transition-colors",
              "text-text-secondary hover:bg-surface-hover hover:text-text-primary cursor-pointer",
              isAdminPath && "bg-surface-elevated text-foreground",
            )}
            aria-label="Round admin"
          />
        }
      >
        <Settings className="h-5 w-5" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60 min-w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col">
            <span>Round Admin</span>
            <span className="text-xs text-muted-foreground font-normal truncate">
              {roundName}
            </span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {adminNavItems.map((item) => (
          <div key={item.href}>
            {item.separatorBefore && <DropdownMenuSeparator />}
            <DropdownMenuItem
              render={
                <Link
                  href={item.href}
                  className="flex items-center gap-2 cursor-pointer"
                />
              }
            >
              <item.icon className="h-4 w-4" />
              <span>{item.label}</span>
            </DropdownMenuItem>
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function SiteHeader() {
  const teamsEnabled = useFeatureFlagEnabled("teams") === true;
  const [location, setLocation] = useLocation();
  const { data: effectiveUser, isLoading } = useEffectiveUser();
  const isDeveloper = isDeveloperEmail(effectiveUser?.email);
  const { value } = useImpersonateContext();
  const phases = useEffectiveRoundPhases();
  const { isAdmin, isFunder, isRecommender } = useRoundAdmin();
  // Acting-as keyed the same way useRoundAdmin resolves isFunder/isAdmin
  // above, so an admin's View-As preview shows exactly what the restricted
  // funder would see (no Review nav, no video queue).
  const isRestrictedFunder = value
    ? isRestrictedFunderViewer(
        value.round,
        value.mode.viewAsUserId ?? value.underlyingUserId,
      )
    : false;
  const roundId = value?.round.id ?? null;
  const { isVisited, markVisited } = useVisitedNavItems(roundId);
  const inRound = roundId != null;
  // Show Back when the user is on a round sub-page AND there's somewhere
  // to go back to. `history.length > 1` filters out fresh loads where
  // tapping back would no-op into "/" — the affordance should only appear
  // when it would actually do something useful.
  const onRoundSubPage =
    inRound && roundId != null && location.startsWith(`/round/${roundId}/`);
  // iPad Capacitor only — desktop has the browser's own back button + the
  // hamburger nav, so the in-header chevron is iPad-specific affordance.
  const canGoBack =
    onRoundSubPage &&
    typeof window !== "undefined" &&
    window.history.length > 1 &&
    Capacitor.isNativePlatform() &&
    isIPad();

  const effectivePhase = pickEffectivePhase(phases);
  const visibility = useMemo(
    () => getNavVisibility(effectivePhase, { isAdmin, isFunder }),
    [effectivePhase, isAdmin, isFunder],
  );
  const phaseLabel = effectivePhase ? PHASE_LABEL[effectivePhase] : null;
  const roundName = value?.round.name ?? null;

  const userNavLinks = [
    {
      label: "Profile",
      href: "/profile",
      icon: UserIcon,
    },
    ...(effectiveUser?.isSfcAdmin
      ? [{ label: "Admin Tools", href: "/admin", icon: SettingsIcon }]
      : []),
    ...(isDeveloper
      ? [{ label: "Dev Tools", href: "/dev", icon: WrenchIcon }]
      : []),
  ];

  // Per-tab memory for the funder Review/Decide nav links. These tabs used to
  // be a pill toggle inside the funder page (which restored each side's last
  // rail/item via an in-page ref); now they're top-bar links, so the place
  // would reset on every switch. We persist the current funder sub-path here
  // and target the remembered one when building each link.
  const funderPrefix = roundId ? `/round/${roundId}/funder/` : null;
  const currentFunderSuffix =
    funderPrefix && location.startsWith(funderPrefix)
      ? location.slice(funderPrefix.length)
      : null;
  const currentFunderTab: WorkspaceTab | null = (() => {
    const seg = currentFunderSuffix?.split("/")[0];
    return seg === "review" || seg === "decide" ? seg : null;
  })();
  useEffect(() => {
    if (roundId && currentFunderTab && currentFunderSuffix) {
      rememberFunderTabPath(roundId, currentFunderTab, currentFunderSuffix);
    }
  }, [roundId, currentFunderTab, currentFunderSuffix]);

  // The whole round nav (Setup dropdown included) renders once both the round
  // and the user record are loaded. Online the user query resolves before the
  // round aggregate, and offline the iPad serves a cached identity, so the
  // user-less window doesn't occur in practice.
  const isSfcAdmin = effectiveUser?.isSfcAdmin === true;
  const hasEffectiveUser = effectiveUser != null;
  const actingAsUserId =
    value?.mode.viewAsUserId ?? value?.underlyingUserId ?? "";
  const navEntries = useMemo(
    () =>
      roundId && hasEffectiveUser
        ? buildRoundNavEntries({
            actingAsUserId,
            roundId,
            visibility,
            isSfcAdmin,
            isSfcRound: value?.round.isSfcRound ?? false,
            isRoundAdmin: isAdmin,
            isFunder,
            isRestrictedFunder,
            location,
            currentFunderTab,
          })
        : [],
    [
      roundId,
      hasEffectiveUser,
      value?.round.isSfcRound,
      actingAsUserId,
      visibility,
      isSfcAdmin,
      isAdmin,
      isFunder,
      isRestrictedFunder,
      location,
      currentFunderTab,
    ],
  );

  // Tabs that belong to no round (today: the Application Builder). They sit
  // after the round's nav and stay put when you leave a round, which is also
  // why they're the one part of the nav that renders outside one.
  const globalNavEntries = useMemo(
    () => buildGlobalNavEntries({ isSfcAdmin, teamsEnabled }),
    [isSfcAdmin, teamsEnabled],
  );

  const showOnboardingBanner =
    inRound && roundId && effectivePhase === "1.onboarding";

  // All platforms render the shared <AppBar> on a single path. iPhone-Capacitor
  // never reaches here (App.tsx routes it to IPhoneApp first); iPad-Capacitor
  // and every browser context (desktop + mobile web) use this. The iPad-only
  // affordances stay individually gated: the "Videos" nav link (above), the
  // back chevron (via `canGoBack`), and the `pt-sa-0` safe-area padding (a
  // no-op where the inset is 0). The AppBar handles its own responsive
  // collision-packing + hamburger drawer.
  //
  // Def → AppBar item mapping: phase visibility becomes
  // disabled/dimmed/tooltip, and the visited store drives the unseen dot.
  // Memoized so the AppBar receives the SAME array while nothing changed:
  // its nav-collision hook is keyed on nav content, but handing it a stable
  // reference on the (many) renders that don't touch the nav is the caller's
  // side of that contract and skips its signature work entirely (SP-2649
  // review). isVisited/markVisited are useCallback-stable per visited set,
  // so a visit still rebuilds the list — the unseen dots must move.
  const navLinks: AppBarNavItem[] = useMemo(() => {
    const toNavLink = (item: RoundNavLinkDef) => ({
      label: item.label,
      href: item.href,
      activePath: item.activePath,
      disabled: item.visibility.state === "disabled",
      dimmed: item.visibility.state === "dimmed",
      tooltip: item.visibility.tooltip,
      unseen: item.visibility.state === "active" && !isVisited(item.key),
      onActivate: () => markVisited(item.key),
      defaultFolded: item.defaultFolded,
    });
    return [
      ...navEntries.map((entry) =>
        entry.kind === "dropdown"
          ? {
              kind: "dropdown" as const,
              label: entry.label,
              activePath: entry.activePath,
              // Aggregate dot on the dropdown trigger when any active child is
              // unseen — mirrors SetupNavDropdown's parent-button behavior.
              unseen: entry.items.some(
                (item) =>
                  item.visibility.state === "active" && !isVisited(item.key),
              ),
              items: entry.items.map(toNavLink),
              defaultFolded: entry.defaultFolded,
            }
          : toNavLink(entry),
      ),
      ...globalNavEntries.map((entry) => ({
        label: entry.label,
        href: entry.href,
      })),
    ];
  }, [navEntries, globalNavEntries, isVisited, markVisited]);
  // In visual-regression e2e runs, suppress the socket-derived header
  // indicators (viewer presence + connection status) so their real-time,
  // timing-dependent state doesn't produce false snapshot diffs.
  const hideLiveIndicators = hideLiveHeaderIndicators();
  return (
    <>
      <AppBar
        // Environment tint: staging/preview and dev builds recolor the header
        // (fills the iPad status-bar region too, since the bar is fixed and
        // safe-area-padded); production keeps the plain surface.
        className={cn("pt-sa-0", getHeaderEnvironmentTintClass())}
        logo={EnvironmentBadgedLogo}
        title={roundName ?? "S-Process"}
        subtitle={phaseLabel ?? undefined}
        titleHref={roundId ? `/round/${roundId}` : "/"}
        logoHref="/"
        pathname={location}
        link={Link}
        adminBadge={
          !isLoading && effectiveUser?.isSfcAdmin ? (
            <span className="inline-flex items-center gap-1 text-label-sm uppercase tracking-wide text-purple-text">
              <ShieldCheckIcon className="size-3" />
              Admin
            </span>
          ) : undefined
        }
        backOnClick={canGoBack ? () => window.history.back() : undefined}
        navLinks={navLinks}
        actions={
          <>
            {inRound && !hideLiveIndicators && <PresenceAvatars />}
            <ImpersonateAction />
            {roundId &&
              (isFunder || effectiveUser?.isSfcAdmin) &&
              !isRestrictedFunder && (
                <>
                  <TodoDrawerTrigger />
                  <QueueDrawer />
                </>
              )}
            {roundId && isRecommender && (
              <button
                type="button"
                title="Your general round notes"
                aria-label="Your general round notes"
                onClick={() => openGeneralNotes()}
                className="relative flex h-9 w-9 items-center justify-center rounded-full text-text-secondary transition-colors hover:bg-surface-hover hover:text-text-primary"
              >
                <NotebookPenIcon className="h-5 w-5" />
                {!isVisited(GENERAL_NOTES_NAV_KEY) && (
                  <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-blue-500" />
                )}
              </button>
            )}
            {roundId && <AdminOrFunderGear roundId={roundId} />}
            {!hideLiveIndicators && <ConnectionStatusDisplay />}
            {!effectiveUser && !isLoading && <LoginButton />}
          </>
        }
        user={
          effectiveUser
            ? { name: effectiveUser.userName, email: effectiveUser.email }
            : undefined
        }
        userNavLinks={userNavLinks}
        onSignOut={() => setLocation(getLogoutLink())}
        // Dev-only build badge (branch + commit + build time) in the account
        // menu. import.meta.env.DEV is true exactly when the app is served by a
        // Vite dev server — local `bun run dev` and on-device `dev:device` —
        // and false on bundled staging/production builds.
        userMenuFooter={import.meta.env.DEV ? <DevBuildInfo /> : undefined}
      />
      {showOnboardingBanner && roundId && (
        <OnboardingBanner roundId={roundId} />
      )}
      {roundId && <OfflineBanner roundId={roundId} />}
      <ArchivedRoundBanner />
      <ImpersonationBanner />
    </>
  );
}
